import React, { useRef, memo, useEffect } from 'react';
import { Card, Form, Space, Spin, Typography, Button, Checkbox, message, Radio } from 'antd';
import { useAsync } from 'react-async-hook';
import { useDispatch } from 'react-redux';
import useProviderEditDisabled from 'hooks/useProviderEditDisabled';
import { ADD_NAVY_HCC_STEPS, ADD_AIR_FORCE_HCC_STEPS } from 'constants';
import commonService from 'services/common.service';
import providerService from 'services/providers.service';
import { actions as centerActions } from 'features/add-center';
import { sleep } from 'utils';
import { PROVIDER_APPLICATION_PROGRAM_TYPES } from 'containers/Provider/SelectProgram/SelectProgram';

const { Title } = Typography;

const HCCProgramInfo = ({
  application,
  setApplication,
  id,
  setStep,
  isFormSubmitting,
  loading,
  applicationSubmitted,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const isSubmitDisabled = useProviderEditDisabled();
  const isAirForce = application?.programType.id === PROVIDER_APPLICATION_PROGRAM_TYPES.AIR_FORCE_HCC.id;

  // Keeps track of whether `next` is pressed or `save`
  const shouldGoToNextStep = useRef(false);

  const { result: ageGroups = [] } = useAsync(async () => {
    try {
      return await commonService.getAgeGroups();
    } catch (error) {
      newrelic.noticeError(error);
      message.error('Age groups not found.', 5);
      return [];
    }
  });

  useEffect(() => {
    if (application.ageGroups && ageGroups) {
      const checkedGroups = application.ageGroups.map((group) => group.id);
      form.setFieldsValue({
        ageGroups: checkedGroups,
        hccInvite: application.hccInvite,
      });
    }
  }, [application, ageGroups, form]);

  return (
    <div id="ages-served" data-testid="agesServed">
      <Spin spinning={isFormSubmitting || loading}>
        <Form
          layout="vertical"
          form={form}
          disabled={applicationSubmitted}
          scrollToFirstError
          onFinish={async (values) => {
            const ageGroups = values.ageGroups.map((group) => ({ id: group }));

            const data = {
              ageGroups: ageGroups,
              hccInvite: values.hccInvite,
            };

            try {
              dispatch(centerActions.setIsFormSubmitting(true));
              const updatedCenterData = await providerService.updateApplication(id, { ...application, ...data });

              setApplication(updatedCenterData);

              if (shouldGoToNextStep.current) {
                setStep(isAirForce ? ADD_AIR_FORCE_HCC_STEPS.CREDENTIALS : ADD_NAVY_HCC_STEPS.HOURS_OF_OPERATION);
              }

              message.success('Saved');
              await sleep(100);
            } catch (error) {
              newrelic.noticeError(error);
              message.error('Unable to save center details');
            } finally {
              dispatch(centerActions.setIsFormSubmitting(false));
            }
          }}
        >
          <Title level={1}>Program info</Title>
          <Card>
            <Form.Item
              name="hccInvite"
              label="were you invited to apply for this program?"
              rules={[{ required: true, message: 'Selection is required' }]}
            >
              <Radio.Group data-testid="invitedToApply">
                <Space direction="vertical">
                  <Radio value={true} data-testid="yesRadio">
                    Yes
                  </Radio>
                  <Radio value={false} data-testid="noRadio">
                    No
                  </Radio>
                </Space>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="ageGroups"
              label="which age groups do you serve at this location?"
              rules={[{ required: true, message: 'Age groups served is required' }]}
              className="mb-0"
            >
              <Checkbox.Group data-testid="checkboxGroup">
                <Space direction="vertical">
                  {ageGroups?.map((group) => (
                    <Checkbox value={group.id} data-testid={`${group.title}`}>
                      {group.description}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </Form.Item>
          </Card>
          <div className="flex space-x-2 mt-4">
            <div className="flex-grow"></div>
            {!applicationSubmitted && (
              <Button
                style={{ borderRadius: '0px' }}
                htmlType="submit"
                onClick={() => {
                  shouldGoToNextStep.current = false;
                }}
                disabled={isFormSubmitting || isSubmitDisabled}
                loading={isFormSubmitting}
              >
                Save
              </Button>
            )}

            <Button
              style={{ borderRadius: '0px' }}
              type="primary"
              role="button"
              htmlType={!applicationSubmitted ? 'submit' : undefined}
              onClick={() => {
                if (applicationSubmitted) {
                  setStep(isAirForce ? ADD_AIR_FORCE_HCC_STEPS.CREDENTIALS : ADD_NAVY_HCC_STEPS.HOURS_OF_OPERATION);
                  return;
                }

                shouldGoToNextStep.current = true;
              }}
              disabled={isFormSubmitting || isSubmitDisabled}
              loading={isFormSubmitting}
              data-testid="program-info-form-submit"
            >
              Next
            </Button>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default memo(HCCProgramInfo);
